import { Bell, Loader2 } from 'lucide-react';
import type { MutedAlertState } from '@kirin/api-client/contracts/alerts';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { trackEvent } from '@/lib/analytics';
import { formatMutedTooltipLines } from '@/lib/mutedAlertFormatting';
import { useUnmuteAlert, type AlertMuteScope } from '@/hooks/api/useAlertMutes';
import { MUTE_BUTTON_WIDTH_CLASS } from '@/components/AlertMuteButton';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';

/**
 * Props for {@link AlertUnmuteButton}.
 */
export interface AlertUnmuteButtonProps {
  /** Scope of the alert (org-shared or self/account). */
  scope: AlertMuteScope;
  /** The alert key to unmute. */
  alertKey: string;
  /** Optional className passed to the trigger Button. */
  className?: string;
  /** Optional callback invoked after a successful unmute. */
  onUnmuted?: () => void;
  /**
   * Optional mute fragment from the grouped alert. When provided and the
   * alert is currently muted, the trigger renders inside a tooltip describing
   * who muted it, when, and until when. Passing `null`/`undefined` (or an
   * already-unmuted state) keeps the bare button — the tooltip is purely
   * additive metadata, never required for the unmute action itself.
   */
  mute?: MutedAlertState | null;
  /**
   * Optional analytics dimensions threaded into the `alert_unmuted` Mixpanel
   * event. See {@link AlertMuteButton} for the rationale.
   */
  analyticsContext?: {
    severity?: string;
    type?: string;
  };
}

/**
 * One-click control to unmute a previously muted alert.
 *
 * Identical gating to {@link AlertMuteButton}: gated by the `alert_muting`
 * feature flag (returns `null` while loading or disabled) and, for org-scope,
 * by `useIsOwnerOrManager` so members cannot unmute org-shared alerts.
 *
 * Wraps `mutateAsync` with the shared {@link createMutationHandler} so users
 * see a consistent success/error toast across every consumer surface.
 */
export function AlertUnmuteButton({
  scope,
  alertKey,
  className,
  onUnmuted,
  mute,
  analyticsContext,
}: AlertUnmuteButtonProps): React.ReactElement | null {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_MUTING,
  ]);
  const mutingEnabled = flags[flagsmithFeatureFlags.ALERT_MUTING] === true;

  const writeGate = useOrgWriteGate(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );

  const unmuteMutation = useUnmuteAlert(scope);
  const { toast } = useToast();

  const handleUnmute = createMutationHandler(unmuteMutation, toast, {
    successMessage: 'Alert unmuted',
    errorMessage: 'Failed to unmute alert',
  });

  if (flagsLoading || !mutingEnabled) {
    return null;
  }

  if (scope.kind === 'organization') {
    if (writeGate.isLoading) return null;
    if (writeGate.hidden) return null;
  }
  const writeDisabled = scope.kind === 'organization' && writeGate.disabled;

  const handleClick = async (): Promise<void> => {
    try {
      await handleUnmute({ alertKey });
      // Both side effects below run only on a confirmed mutation success.
      // They are deliberately ISOLATED from each other: a throw in one must
      // never block the other (telemetry/UX divergence is much worse than a
      // single skipped event). Order: callback first (user-observable), then
      // analytics (background).
      try {
        onUnmuted?.();
      } catch {
        // Caller-supplied callbacks are out of our control. Swallowing here
        // ensures a misbehaving consumer can't suppress the analytics event
        // for a mutation the backend has already accepted.
      }
      trackEvent('alert_unmuted', {
        scope: scope.kind,
        alert_key: alertKey,
        severity: analyticsContext?.severity,
        type: analyticsContext?.type,
      });
    } catch {
      // createMutationHandler already toasted; swallow so the click handler
      // does not bubble an unhandled rejection up to React's error boundary.
    }
  };

  const isPending = unmuteMutation.isPending;
  const tooltipLines = formatMutedTooltipLines(mute);

  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(MUTE_BUTTON_WIDTH_CLASS, className)}
      disabled={isPending || writeDisabled}
      aria-label="Unmute alert"
      onClick={() => void handleClick()}
    >
      {isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Bell className="size-4" />
      )}
      <span>Unmute</span>
    </Button>
  );

  if (writeDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
      </Tooltip>
    );
  }

  if (!tooltipLines) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5 text-left">
          <span>Muted by {tooltipLines.muter}</span>
          <span>On {tooltipLines.mutedAt}</span>
          <span>Until {tooltipLines.mutedUntil}</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

AlertUnmuteButton.displayName = 'AlertUnmuteButton';
